import { useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Chip, CircularProgress } from "@mui/material";

import { useBatchDetail } from "../hooks/useBatchDetail";
import { Drawer, DrawerControl, ExpandableText, MetaDrivenTable, PaginationControls } from "../../../shared/components";
import { Visibility } from "@mui/icons-material";
import { ProcessedData } from "./ProcessedData";

const headers = [
    { alias_name: "batch_header_id", label: "Header ID", display_order: 4, is_show: true },
    { alias_name: "row_number", label: "Row Number", display_order: 4, is_show: true },
    { alias_name: "error_count", label: "Error Count", display_order: 4, is_show: true },
    { alias_name: "error_message", label: "Error Message", display_order: 4, is_show: true },
    { alias_name: "success_message", label: "Success Message", display_order: 4, is_show: true },
    { alias_name: "status", label: "Status", display_order: 5, is_show: true },
    { alias_name: "processed_date", label: "Processed Date", display_order: 9, is_show: true },
];

const metrics = {
    description: {
        type: "component",
        element: (row: any) => row.processed_data?.description || row.original_data?.["SITE DESCRIPTION"] || 'N/A'
    },
    status: {
        type: "component",
        element: (row: any) => {
            const color =
                row.status === 'PROCESSED' ? 'success' :
                    row.status === 'ERROR' || row.status === 'FAILED' ? 'error' :
                        row.status === 'PENDING' ? 'warning' : 'default';
            return <Chip label={row.status} color={color} size="small" />;
        }
    },
    object_reference: {
        type: "component",
        element: (row: any) => row.object_reference || '-'
    },
    error_count: {
        type: "component",
        element: (row: any) =>
            row.error_count > 0 ?
                <Chip label={row.error_count} color="error" size="small" /> :
                '0'
    },
    processed_date: {
        type: "date_and_time"
    },
    error_message: {
        type: "component",
        element: (row: any) => <ExpandableText text={row.error_message} />
    }
};

export const BatchDetail = () => {

    const { batchHeaderId } = useParams<{ batchHeaderId: string }>();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);

    const [openDetails, setOpenDetails] = useState<{
        status: boolean;
        data: any;
    }>({
        status: false,
        data: null
    });

    const { data, isLoading, pagination } = useBatchDetail({
        page,
        limit,
        batchHeaderId
    });

    if (isLoading) {
        return (
            <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box py={2} display="flex" gap={2} flexDirection="column" height="100%" overflow="hidden">

            <MetaDrivenTable
                headers={headers}
                data={data}
                dataMetrics={metrics}
                sortDisabled
                actions={[
                    {
                        label: <Visibility />,
                        handleClick: (row: any) => {
                            setOpenDetails({
                                status: true,
                                data: row
                            });
                        }
                    }]}
            />

            {pagination && (
                <Box flexShrink={0} px={2}>
                    <PaginationControls
                        total={pagination.total}
                        rowsPerPage={pagination.limit}
                        currentPage={pagination.page}
                        handlePageChange={(newPage) => {
                            setPage(newPage);
                        }}
                        handleRowsPerPage={(newLimit) => {
                            setPage(1);
                            setLimit(newLimit);
                        }}
                    />
                </Box>
            )}

            <DrawerControl
                title={"Import Details"}
                subTitle=""
                isOpen={openDetails.status}
                handleClose={() => {
                    setOpenDetails({
                        status: false,
                        data: null
                    })
                }}
                children={openDetails.data && <ProcessedData originalData={openDetails.data.original_data} processedData={openDetails.data.processed_data} />}
                filterActions={[
                    {
                        label: 'close',
                        variant: 'outlined',
                        handleClick: () => {
                            setOpenDetails({
                                status: false,
                                data: null
                            });
                        }
                    }
                ]}
            />

        </Box>
    );
};
